/**
 * Agreement Rate Utilities
 * Handles fetching and processing agreement rate data for evaluation forms
 */

import { QualityApi } from "@genesys/cloud-frontend/apis/QualityApi";
import {
    EvaluationSearchResponse,
    EvaluationStatusEnum,
} from "@genesys/cloud-frontend/models";
import { AgreementRateCalculator } from "./agreementRateCalculator";
import { API_CONFIGURATION } from "./apiUtils";

import { AgreementRateData } from "../features/qualityForm/qualityFormSlices/qualityFormStates";

// Response interface
export interface AgreementRateResponse {
    success: boolean;
    data?: AgreementRateData;
    error?: string;
}

// Constants for Agreement Rate Calculations
export const DEFAULT_DATE_RANGE_PERIOD = 90;
/**
 * Gets the date range
 * Used for filtering evaluations in agreement rate calculations
 *
 * @returns DateRange object with start and end dates in ISO format
 */
function getDateRange(): {
    startDate: string;
    endDate: string;
} {
    const endDate = new Date();
    const startDate = new Date();
    const dateRange = DEFAULT_DATE_RANGE_PERIOD;

    startDate.setDate(endDate.getDate() - dateRange);

    return {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
    };
}

/**
 * Creates empty agreement rate data structure
 *
 * @param formId - Published version ID (optional)
 * @returns Empty agreement rate data
 */
export function createEmptyAgreementRateData(
    formId?: string
): AgreementRateData {
    return {
        agreementRate: 0,
        acceptedSuggestions: 0,
        totalSuggestions: 0,
        hasData: false,
        formId,
    };
}

/**
 * Checks if the given form ID represents the latest published version
 *
 * @param formId - The form ID to check
 * @returns Promise<boolean> - true if the form is the latest published version
 */
export async function isLatestPublishedVersion(
    formId: string
): Promise<boolean> {
    try {
        const qualityApi = new QualityApi(API_CONFIGURATION);

        // Call the published forms API with the current form ID
        const publishedResponse =
            await qualityApi.getQualityPublishedformsEvaluation({
                formId: formId,
            });

        // If the response ID matches the input ID, then this is the latest published version
        return publishedResponse?.id === formId;
    } catch (error) {
        console.error(
            "Error checking if form is latest published version:",
            error
        );
        return false;
    }
}

/**
 * Build form-level search request for agreement rate calculations
 * Creates a properly formatted request for form-level agreement rate data
 *
 * @param formId - The form ID to search for
 * @param dateRange - date range
 * @returns Formatted search request object
 */
function buildFormAgreementRateSearchRequest(
    formId: string,
    dateRange: any
): any {
    if (!formId) {
        throw new Error("Form ID is required for agreement rate search");
    }

    if (dateRange && (!dateRange.startDate || !dateRange.endDate)) {
        throw new Error(
            "Invalid date range provided for agreement rate search"
        );
    }

    // Build base query filters
    const queryFilters = [
        {
            type: "EXACT",
            field: "evaluationStatus",
            values: [EvaluationStatusEnum.FINISHED],
        },
        {
            type: "DATE_RANGE",
            field: "submittedDate",
            startValue: dateRange.startDate,
            endValue: dateRange.endDate,
        },
        {
            type: "EXACT",
            field: "formId",
            value: formId,
        },
    ];

    return {
        pageNumber: 1,
        query: queryFilters,
        aggregations: [
            {
                name: "eaAcceptedSuggestionCount",
                field: "eaAcceptedSuggestionCount",
                type: "SUM",
            },
            {
                name: "eaSuggestionCount",
                field: "eaSuggestionCount",
                type: "SUM",
            },
            {
                name: "aiAcceptedSuggestionCount",
                field: "aiAcceptedSuggestionCount",
                type: "SUM",
            },
            {
                name: "aiSuggestionCount",
                field: "aiSuggestionCount",
                type: "SUM",
            },
        ],
    };
}

/**
 * Extracts aggregation data from API response
 *
 * @param apiResponse - API response containing aggregations
 * @returns Aggregation data or null if not found
 */
function extractFormAggregationData(apiResponse: EvaluationSearchResponse) {
    try {
        if (!apiResponse?.aggregations) {
            return null;
        }

        // Check if aggregations object has the expected properties
        const hasAggregationData =
            apiResponse.aggregations.eaAcceptedSuggestionCount !== undefined ||
            apiResponse.aggregations.eaSuggestionCount !== undefined ||
            apiResponse.aggregations.aiAcceptedSuggestionCount !== undefined ||
            apiResponse.aggregations.aiSuggestionCount !== undefined;

        if (!hasAggregationData) {
            return null;
        }

        return {
            eaAcceptedSuggestionCount:
                apiResponse.aggregations.eaAcceptedSuggestionCount,
            eaSuggestionCount: apiResponse.aggregations.eaSuggestionCount,
            aiAcceptedSuggestionCount:
                apiResponse.aggregations.aiAcceptedSuggestionCount,
            aiSuggestionCount: apiResponse.aggregations.aiSuggestionCount,
        };
    } catch (error) {
        console.error("Error extracting form aggregation data:", error);
        return null;
    }
}

/**
 * Processes the API response and calculates agreement rate data
 *
 * @param apiResponse - Raw API response from evaluation search
 * @param formId - The published version ID used for the search
 * @returns Processed agreement rate data
 */
function processFormAgreementRateResponse(
    apiResponse: EvaluationSearchResponse,
    formId: string
): AgreementRateData {
    try {
        // Extract aggregation data from API response
        const aggregationData = extractFormAggregationData(apiResponse);

        // Handle case where no aggregation data is found
        if (!aggregationData) {
            return createEmptyAgreementRateData(formId);
        }

        // Extract suggestion counts from aggregation data
        const calculationData = {
            eaAcceptedSuggestionCount:
                aggregationData.eaAcceptedSuggestionCount?.value || 0,
            eaSuggestionCount: aggregationData.eaSuggestionCount?.value || 0,
            aiAcceptedSuggestionCount:
                aggregationData.aiAcceptedSuggestionCount?.value || 0,
            aiSuggestionCount: aggregationData.aiSuggestionCount?.value || 0,
        };

        // Calculate agreement rate using the calculator utility
        const calculationResult =
            AgreementRateCalculator.calculateFormAgreementRate(calculationData);

        const finalResult = {
            agreementRate: calculationResult.agreementRate,
            acceptedSuggestions: calculationResult.acceptedSuggestions,
            totalSuggestions: calculationResult.totalSuggestions,
            hasData: calculationResult.hasData,
            formId,
        };

        return finalResult;
    } catch (error) {
        console.error("Error processing form agreement rate response:", error);
        return createEmptyAgreementRateData(formId);
    }
}

/**
 * Fetches form-level agreement rate data
 *
 * @param formId - The form ID to get agreement rate data for
 * @returns Promise resolving to agreement rate response
 */
export async function getFormAgreementRate(
    formId: string
): Promise<AgreementRateResponse> {
    try {
        // Validate input parameters
        if (!formId || typeof formId !== "string" || formId.trim() === "") {
            return {
                success: false,
                error: "Form ID is required and must be a non-empty string",
            };
        }

        const effectiveDateRange = getDateRange();

        const searchRequest = buildFormAgreementRateSearchRequest(
            formId,
            effectiveDateRange
        );

        const qualityApi = new QualityApi(API_CONFIGURATION);

        // Use QualityApi to search evaluations for agreement rate calculation
        const apiResponse = await qualityApi.postQualityEvaluationsSearch({
            body: searchRequest,
        });

        // Process API response
        const agreementRateData = processFormAgreementRateResponse(
            apiResponse,
            formId
        );

        return {
            success: true,
            data: agreementRateData,
        };
    } catch (error) {
        console.error("Error fetching form agreement rate:", error);
        return {
            success: false,
            error: "An error occurred while fetching agreement rate data",
        };
    }
}
